<script setup lang="ts">
import { ref, watch } from "vue";

const props = defineProps<{
  amount: number | null;
  amountInWords: string;
  inputLabel: string;
  resetLabel: string;
}>();

const emit = defineEmits<{
  (e: "set-amount", value: number | null): void;
  (e: "increment", value: number): void;
  (e: "decrement", value: number): void;
  (e: "reset"): void;
}>();

const inputValue = ref("");

// Unique id for input/label association (keeps markup accessible)
const inputId = `zakat-input-${Math.random().toString(36).slice(2, 9)}`;

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-PK").format(value);
}

watch(
  () => props.amount,
  (value) => {
    inputValue.value = value && value > 0 ? formatNumber(value) : "";
  },
  { immediate: true },
);

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  const digitsOnly = target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

  if (!digitsOnly) {
    inputValue.value = "";
    emit("set-amount", null);
    return;
  }

  const numeric = parseInt(digitsOnly, 10);
  if (Number.isNaN(numeric) || numeric <= 0) {
    inputValue.value = "";
    emit("set-amount", null);
    return;
  }

  inputValue.value = formatNumber(numeric);
  emit("set-amount", numeric);
}
</script>

<template>
  <section class="space-y-4">
    <label
      :for="inputId"
      class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 text-center leading-snug"
    >
      {{ inputLabel }}
    </label>

    <div
      class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-4"
    >
      <div
        class="text-center text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1.5 leading-none"
      >
        PKR
      </div>
      <input
        :id="inputId"
        :value="inputValue"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        :aria-label="inputLabel"
        :aria-describedby="amountInWords ? inputId + '-inwords' : null"
        class="w-full bg-transparent text-center text-3xl sm:text-4xl font-semibold text-gray-900 dark:text-gray-100 outline-none"
        placeholder="0"
        @input="handleInput"
      />
    </div>

    <p
      :id="inputId + '-inwords'"
      class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center min-h-4 leading-snug"
    >
      {{ amountInWords ? `${amountInWords} rupees` : "" }}
    </p>

    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <button
        class="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800"
        @click="emit('increment', 10000)"
      >
        +10k
      </button>
      <button
        class="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800"
        @click="emit('increment', 50000)"
      >
        +50k
      </button>
      <button
        class="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800"
        @click="emit('increment', 100000)"
      >
        +1 lakh
      </button>
      <button
        class="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800"
        @click="emit('decrement', 10000)"
      >
        -10k
      </button>
    </div>

    <div class="text-center">
      <button
        class="text-sm text-gray-500 dark:text-gray-400 hover:underline underline-offset-2"
        @click="emit('reset')"
      >
        {{ resetLabel }}
      </button>
    </div>
  </section>
</template>
